/**
 * Adaptive profile (user_type branching) + legacy education fields for AI backward compatibility.
 */

export const USER_TYPES = [
  "school_student",
  "senior_secondary_student",
  "college_student",
  "working_professional",
  "career_switcher",
] as const;

export type UserType = (typeof USER_TYPES)[number];

export const EXAM_STATES = [
  "preparing_for_exams",
  "waiting_for_results",
  "results_received",
] as const;

export type ExamState = (typeof EXAM_STATES)[number];

export const SCHOOL_CLASSES = [
  "class_6",
  "class_7",
  "class_8",
  "class_9",
  "class_10",
] as const;

export const SUBJECT_CHIPS = [
  { id: "science", label: "Science" },
  { id: "math", label: "Math" },
  { id: "english", label: "English" },
  { id: "social_studies", label: "Social studies" },
  { id: "languages", label: "Languages" },
  { id: "computers", label: "Computers / IT" },
  { id: "arts", label: "Arts / music" },
  { id: "sports", label: "Sports / PE" },
] as const;

export const SENIOR_STREAMS = [
  { id: "science", label: "Science" },
  { id: "commerce", label: "Commerce" },
  { id: "arts", label: "Arts / Humanities" },
  { id: "vocational", label: "Vocational" },
  { id: "undecided", label: "Undecided" },
] as const;

/** DB + guard shape: legacy + adaptive columns */
export type ProfileCompletionRow = {
  life_phase?: string | null;
  education_level?: string | null;
  education_stream?: string | null;
  current_class?: string | null;
  user_type?: string | null;
  age?: number | null;
  city?: string | null;
  gender?: string | null;
  exam_state?: string | null;
  direction_confidence?: string | null;
  help_needed?: string | null;
  main_confusion_area?: string | null;
  favorite_subjects?: unknown;
  least_favorite_subjects?: unknown;
  degree?: string | null;
  year_of_study?: string | null;
  specialization?: string | null;
  current_role?: string | null;
  years_of_experience?: string | null;
  target_direction?: string | null;
  family_background?: string | null;
  additional_note?: string | null;
};

export const PROFILE_GUARD_SELECT =
  "life_phase, education_level, education_stream, current_class, user_type, age, city, gender, exam_state, direction_confidence, help_needed, main_confusion_area, favorite_subjects, least_favorite_subjects, degree, year_of_study, specialization, current_role, years_of_experience, target_direction, family_background, additional_note";

function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string" && x.length > 0);
}

function isEducationStreamRequiredLegacy(
  educationLevel: string | null | undefined,
): boolean {
  const L = educationLevel?.trim();
  return L === "12th" || L === "graduation";
}

export function isLegacyProfileComplete(
  p: ProfileCompletionRow | null | undefined,
): boolean {
  if (!p) return false;
  if (!p.life_phase?.trim()) return false;
  if (!p.education_level?.trim()) return false;
  if (!p.current_class?.trim()) return false;
  if (
    isEducationStreamRequiredLegacy(p.education_level) &&
    !p.education_stream?.trim()
  ) {
    return false;
  }
  return true;
}

export function isAdaptiveProfileComplete(
  p: ProfileCompletionRow | null | undefined,
): boolean {
  if (!p?.user_type?.trim()) return false;
  const ut = p.user_type.trim() as UserType;
  if (!USER_TYPES.includes(ut)) return false;
  if (p.age == null || p.age < 10 || p.age > 80) return false;
  if (!p.city?.trim()) return false;
  if (!p.direction_confidence?.trim()) return false;
  if (!p.help_needed?.trim()) return false;
  if (!p.main_confusion_area?.trim()) return false;
  if (!p.family_background?.trim()) return false;

  if (ut === "school_student") {
    if (!p.current_class?.trim()) return false;
    const fav = asStringArray(p.favorite_subjects);
    if (fav.length < 1) return false;
    return true;
  }
  if (ut === "senior_secondary_student") {
    if (!p.education_stream?.trim()) return false;
    if (!p.exam_state?.trim()) return false;
    const cls = p.current_class?.trim() ?? "";
    if (!cls.includes("11") && !cls.includes("12")) return false;
    return true;
  }
  if (ut === "college_student") {
    if (!p.degree?.trim() || !p.year_of_study?.trim()) return false;
    return true;
  }
  if (ut === "working_professional") {
    if (!p.current_role?.trim() || !p.years_of_experience?.trim()) return false;
    return true;
  }
  if (ut === "career_switcher") {
    if (!p.current_role?.trim()) return false;
    return true;
  }
  return false;
}

/** Assessment access: adaptive complete OR legacy-only complete (existing users). */
export function isProfileCompleteForAssessment(
  p: ProfileCompletionRow | null | undefined,
): boolean {
  if (!p) return false;
  if (isAdaptiveProfileComplete(p)) return true;
  if (!p.user_type?.trim() && isLegacyProfileComplete(p)) return true;
  return false;
}

export function shouldBlockAssessmentAccess(
  profile: ProfileCompletionRow | null | undefined,
  hasAnyAssessmentRow: boolean,
): boolean {
  if (hasAnyAssessmentRow) return false;
  return !isProfileCompleteForAssessment(profile);
}

export type AdaptiveFormInput = {
  userType: UserType | "";
  age: string;
  gender: string;
  city: string;
  schoolClass: string;
  favoriteSubjects: string[];
  leastFavoriteSubjects: string[];
  seniorClass: "11" | "12" | "";
  seniorStream: string;
  examState: ExamState | "";
  degree: string;
  yearOfStudy: string;
  specialization: string;
  currentRole: string;
  yearsExperience: string;
  targetDirection: string;
  directionConfidence: string;
  helpNeeded: string;
  mainConfusion: string;
  familyBackground: string;
  additionalNote: string;
};

export function emptyAdaptiveForm(): AdaptiveFormInput {
  return {
    userType: "",
    age: "",
    gender: "",
    city: "",
    schoolClass: "",
    favoriteSubjects: [],
    leastFavoriteSubjects: [],
    seniorClass: "",
    seniorStream: "",
    examState: "",
    degree: "",
    yearOfStudy: "",
    specialization: "",
    currentRole: "",
    yearsExperience: "",
    targetDirection: "",
    directionConfidence: "",
    helpNeeded: "",
    mainConfusion: "",
    familyBackground: "",
    additionalNote: "",
  };
}

export function profileRowToAdaptiveForm(
  p: ProfileCompletionRow | null | undefined,
): AdaptiveFormInput {
  const base = emptyAdaptiveForm();
  if (!p) return base;
  base.userType = (p.user_type as UserType) || "";
  if (p.age != null) base.age = String(p.age);
  base.gender = p.gender ?? "";
  base.city = p.city ?? "";
  base.seniorStream = p.education_stream ?? "";
  base.examState = (p.exam_state as ExamState) || "";
  base.degree = p.degree ?? "";
  base.yearOfStudy = p.year_of_study ?? "";
  base.specialization = p.specialization ?? "";
  base.currentRole = p.current_role ?? "";
  base.yearsExperience = p.years_of_experience ?? "";
  base.targetDirection = p.target_direction ?? "";
  base.directionConfidence = p.direction_confidence ?? "";
  base.helpNeeded = p.help_needed ?? "";
  base.mainConfusion = p.main_confusion_area ?? "";
  base.familyBackground = p.family_background ?? "";
  base.additionalNote = p.additional_note ?? "";
  base.favoriteSubjects = asStringArray(p.favorite_subjects);
  base.leastFavoriteSubjects = asStringArray(p.least_favorite_subjects);

  const cc = p.current_class ?? "";
  if (p.user_type === "school_student" && cc) {
    const m = cc.match(/Class\s*(\d+)/i);
    if (m) {
      const n = m[1];
      if (["6", "7", "8", "9", "10"].includes(n)) {
        base.schoolClass = `class_${n}`;
      }
    }
  }
  if (p.user_type === "senior_secondary_student") {
    if (cc.includes("11")) base.seniorClass = "11";
    else if (cc.includes("12")) base.seniorClass = "12";
  }

  return base;
}

export function validateAdaptiveForm(f: AdaptiveFormInput): string | null {
  if (!f.userType) return "Choose the option that best describes you.";
  const ageN = Number(f.age);
  if (!f.age.trim() || !Number.isFinite(ageN) || ageN < 10 || ageN > 80) {
    return "Enter your age (10–80).";
  }
  if (!f.city.trim()) return "Enter your city.";
  if (!f.directionConfidence.trim()) return "Pick the answer that fits you best.";
  if (!f.helpNeeded.trim()) return "What would you like help with the most?";
  if (!f.mainConfusion.trim()) return "Pick what feels closest right now.";
  if (!f.familyBackground.trim()) return "Choose a family background option (or “Prefer not to say”).";

  if (f.userType === "school_student") {
    if (!f.schoolClass) return "Which class are you in?";
    if (f.favoriteSubjects.length < 1) return "Pick at least one subject you enjoy.";
  }
  if (f.userType === "senior_secondary_student") {
    if (!f.seniorClass) return "Are you in Class 11 or 12?";
    if (!f.seniorStream.trim()) return "Pick your stream.";
    if (!f.examState) return "Where are you with board / entrance exams right now?";
  }
  if (f.userType === "college_student") {
    if (!f.degree.trim()) return "What are you studying (degree / course)?";
    if (!f.yearOfStudy.trim()) return "Which year or semester are you in?";
  }
  if (f.userType === "working_professional") {
    if (!f.currentRole.trim()) return "What is your current role (short is fine)?";
    if (!f.yearsExperience.trim()) return "Roughly how long have you been working?";
  }
  if (f.userType === "career_switcher") {
    if (!f.currentRole.trim()) return "What do you do today (role or situation)?";
  }
  return null;
}

function schoolClassToEducationLevel(classId: string): string {
  const n = Number(classId.replace("class_", ""));
  if (!Number.isFinite(n)) return "below_10th";
  return n >= 10 ? "10th" : "below_10th";
}

function schoolClassLabel(classId: string): string {
  const n = classId.replace("class_", "");
  return `Class ${n}`;
}

/** Profile row plus optional display name (for dossier / report). */
export type ProfileRowForAi = ProfileCompletionRow & {
  name?: string | null;
};

const MAX_AI_PROFILE_DOSSIER_CHARS = 2800;

function escPipe(s: string, max = 400): string {
  return s.replace(/\|/g, "/").replace(/\s+/g, " ").trim().slice(0, max);
}

function pushKv(parts: string[], key: string, value: unknown): void {
  if (value == null) return;
  const t = typeof value === "string" ? value.trim() : String(value).trim();
  if (!t) return;
  parts.push(`${key}=${escPipe(t, 500)}`);
}

/**
 * Full profile dossier for interview, signal extraction preamble, and report summaries.
 * Safe with partial / legacy-only rows (omits missing fields). No prompt/output schema changes.
 */
export function buildAiProfileContextBlock(
  p: ProfileRowForAi | null | undefined,
): string {
  if (!p) return "";
  const parts: string[] = [];
  pushKv(parts, "user_type", p.user_type);
  pushKv(parts, "name", p.name);
  if (p.age != null && p.age >= 10 && p.age <= 100) {
    parts.push(`age=${p.age}`);
  }
  pushKv(parts, "city", p.city);
  pushKv(parts, "gender", p.gender);
  pushKv(parts, "life_phase", p.life_phase);
  pushKv(parts, "education_level", p.education_level);
  pushKv(parts, "education_stream", p.education_stream);
  if (p.education_stream?.trim()) {
    pushKv(parts, "current_stream", p.education_stream);
  }
  pushKv(parts, "current_class", p.current_class);
  pushKv(parts, "degree", p.degree);
  pushKv(parts, "year_of_study", p.year_of_study);
  pushKv(parts, "specialization", p.specialization);
  pushKv(parts, "current_role", p.current_role);
  pushKv(parts, "years_of_experience", p.years_of_experience);
  pushKv(parts, "target_direction", p.target_direction);
  pushKv(parts, "exam_state", p.exam_state);
  pushKv(parts, "direction_confidence", p.direction_confidence);
  pushKv(parts, "help_needed", p.help_needed);
  pushKv(parts, "main_confusion_area", p.main_confusion_area);
  const fav = asStringArray(p.favorite_subjects);
  if (fav.length) parts.push(`favorite_subjects=${escPipe(fav.join(", "), 400)}`);
  const least = asStringArray(p.least_favorite_subjects);
  if (least.length) {
    parts.push(`least_favorite_subjects=${escPipe(least.join(", "), 400)}`);
  }
  pushKv(parts, "family_background", p.family_background);
  if (p.additional_note?.trim()) {
    pushKv(parts, "additional_note", p.additional_note);
  }
  let out = parts.join(" | ");
  if (out.length > MAX_AI_PROFILE_DOSSIER_CHARS) {
    out = `${out.slice(0, MAX_AI_PROFILE_DOSSIER_CHARS - 1)}…`;
  }
  return out;
}

/**
 * Fields not already sent in generate-questions `buildContextPrefix` (legacy line).
 * Keeps the user message compact while still exposing adaptive fields to the model.
 */
export function buildAdaptiveSupplementForQuestionGen(
  p: ProfileCompletionRow | null | undefined,
): string {
  if (!p) return "";
  const parts: string[] = [];
  pushKv(parts, "user_type", p.user_type);
  pushKv(parts, "exam_state", p.exam_state);
  if (p.education_stream?.trim()) {
    pushKv(parts, "current_stream", p.education_stream);
  }
  pushKv(parts, "degree", p.degree);
  pushKv(parts, "year_of_study", p.year_of_study);
  pushKv(parts, "specialization", p.specialization);
  pushKv(parts, "current_role", p.current_role);
  pushKv(parts, "years_of_experience", p.years_of_experience);
  pushKv(parts, "target_direction", p.target_direction);
  pushKv(parts, "direction_confidence", p.direction_confidence);
  pushKv(parts, "help_needed", p.help_needed);
  pushKv(parts, "main_confusion_area", p.main_confusion_area);
  const fav = asStringArray(p.favorite_subjects);
  if (fav.length) parts.push(`favorite_subjects=${escPipe(fav.join(", "), 400)}`);
  const least = asStringArray(p.least_favorite_subjects);
  if (least.length) {
    parts.push(`least_favorite_subjects=${escPipe(least.join(", "), 400)}`);
  }
  if (p.additional_note?.trim()) {
    pushKv(parts, "additional_note", p.additional_note);
  }
  return parts.join(" | ");
}

/** Maps adaptive answers to existing columns used by scoring/report prompts. */
export function deriveLegacyProfileFields(
  f: AdaptiveFormInput,
): {
  life_phase: string;
  education_level: string;
  education_stream: string | null;
  current_class: string;
} {
  const ut = f.userType as UserType;
  if (ut === "school_student") {
    const edu = schoolClassToEducationLevel(f.schoolClass);
    const label = schoolClassLabel(f.schoolClass);
    const fav =
      f.favoriteSubjects.length > 0
        ? `Enjoys: ${f.favoriteSubjects.join(", ")}`
        : "";
    const least =
      f.leastFavoriteSubjects.length > 0
        ? ` · Less keen on: ${f.leastFavoriteSubjects.join(", ")}`
        : "";
    return {
      life_phase: "student",
      education_level: edu,
      education_stream: null,
      current_class: `${label}${fav ? ` · ${fav}` : ""}${least}`,
    };
  }
  if (ut === "senior_secondary_student") {
    const c = f.seniorClass === "11" ? "Class 11" : "Class 12";
    const exam = String(f.examState).replace(/_/g, " ");
    return {
      life_phase: "student",
      education_level: "12th",
      education_stream: f.seniorStream.trim() || null,
      current_class: `${c} · exams: ${exam}`,
    };
  }
  if (ut === "college_student") {
    const spec = f.specialization.trim()
      ? ` · ${f.specialization.trim()}`
      : "";
    return {
      life_phase: "student",
      education_level: "graduation",
      education_stream: f.specialization.trim() || null,
      current_class: `${f.degree.trim()} · ${f.yearOfStudy.trim()}${spec}`,
    };
  }
  if (ut === "working_professional") {
    return {
      life_phase: "working",
      education_level: "other",
      education_stream: null,
      current_class: `${f.currentRole.trim()} · ~${f.yearsExperience.trim()} experience`,
    };
  }
  return {
    life_phase: "pivot",
    education_level: "other",
    education_stream: null,
    current_class: f.targetDirection.trim()
      ? `${f.currentRole.trim()} · exploring: ${f.targetDirection.trim()}`
      : f.currentRole.trim(),
  };
}

/** @deprecated Use buildAdaptiveSupplementForQuestionGen or buildAiProfileContextBlock */
export function buildAdaptiveContextSnippet(p: ProfileCompletionRow | null): string {
  return buildAdaptiveSupplementForQuestionGen(p);
}

/**
 * Extra profile line for report payloads: avoids duplicating basicProfileSummary + context
 * (age, city, life_phase, education_*, current_class, family_background already passed separately).
 */
export function buildReportAdaptiveProfileSupplement(
  p: ProfileCompletionRow | null | undefined,
): string {
  if (!p) return "";
  const parts: string[] = [];
  pushKv(parts, "gender", p.gender);
  pushKv(parts, "user_type", p.user_type);
  pushKv(parts, "exam_state", p.exam_state);
  if (p.education_stream?.trim()) {
    pushKv(parts, "current_stream", p.education_stream);
  }
  pushKv(parts, "degree", p.degree);
  pushKv(parts, "year_of_study", p.year_of_study);
  pushKv(parts, "specialization", p.specialization);
  pushKv(parts, "current_role", p.current_role);
  pushKv(parts, "years_of_experience", p.years_of_experience);
  pushKv(parts, "target_direction", p.target_direction);
  pushKv(parts, "direction_confidence", p.direction_confidence);
  pushKv(parts, "help_needed", p.help_needed);
  pushKv(parts, "main_confusion_area", p.main_confusion_area);
  const fav = asStringArray(p.favorite_subjects);
  if (fav.length) parts.push(`favorite_subjects=${escPipe(fav.join(", "), 400)}`);
  const least = asStringArray(p.least_favorite_subjects);
  if (least.length) {
    parts.push(`least_favorite_subjects=${escPipe(least.join(", "), 400)}`);
  }
  if (p.additional_note?.trim()) {
    pushKv(parts, "additional_note", p.additional_note);
  }
  let out = parts.join(" | ");
  if (out.length > MAX_AI_PROFILE_DOSSIER_CHARS) {
    out = `${out.slice(0, MAX_AI_PROFILE_DOSSIER_CHARS - 1)}…`;
  }
  return out;
}

/** @alias for report pipeline */
export function buildAdaptiveProfileSummaryForReport(
  p: ProfileCompletionRow | null,
): string {
  return buildReportAdaptiveProfileSupplement(p);
}

export function adaptiveFormToUpsertRow(
  userId: string,
  name: string,
  email: string,
  f: AdaptiveFormInput,
  promo?: { promo_code: string; promo_code_type: string },
): Record<string, unknown> {
  const legacy = deriveLegacyProfileFields(f);
  const ut = f.userType as UserType;
  const row: Record<string, unknown> = {
    id: userId,
    name,
    email,
    age: Number(f.age),
    city: f.city.trim() || null,
    gender: f.gender.trim() || null,
    family_background: f.familyBackground.trim() || null,
    life_phase: legacy.life_phase,
    education_level: legacy.education_level,
    education_stream: legacy.education_stream,
    current_class: legacy.current_class,
    user_type: ut,
    exam_state: ut === "senior_secondary_student" ? f.examState || null : null,
    direction_confidence: f.directionConfidence.trim() || null,
    help_needed: f.helpNeeded.trim() || null,
    main_confusion_area: f.mainConfusion.trim() || null,
    favorite_subjects: ut === "school_student" ? f.favoriteSubjects : [],
    least_favorite_subjects:
      ut === "school_student" ? f.leastFavoriteSubjects : [],
    degree: ut === "college_student" ? f.degree.trim() || null : null,
    year_of_study: ut === "college_student" ? f.yearOfStudy.trim() || null : null,
    specialization: ut === "college_student" ? f.specialization.trim() || null : null,
    current_role:
      ut === "working_professional" || ut === "career_switcher"
        ? f.currentRole.trim() || null
        : null,
    years_of_experience:
      ut === "working_professional" ? f.yearsExperience.trim() || null : null,
    target_direction:
      ut === "career_switcher" ? f.targetDirection.trim() || null : null,
    additional_note: f.additionalNote.trim() || null,
    updated_at: new Date().toISOString(),
  };
  if (promo) {
    row.promo_code = promo.promo_code;
    row.promo_code_type = promo.promo_code_type;
  }
  return row;
}
